import React, { useState, useEffect } from 'react';
import propertyService from '../../services/propertyService';
import '../admin/AdminDashboard.css';

const AgentPropertyForm = ({ agentInfo, property, onPropertyAdded, onInvitations }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const isEditMode = !!property;

  
  // Form state
  const [propertyData, setPropertyData] = useState({
    address: '',
    neighborhood: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    lotSize: '',
    description: '',
    propertyType: 'House',
    listingType: 'purchase',
    yearBuilt: '',
    propertyStatus: 'For Sale',
    features: '',
    allowsDogs: false,
    allowsCats: false,
    petDeposit: '0',
    petRent: '0',
    petRestrictions: '',
    parkingType: 'None',
    parkingSpaces: '0',
    laundryType: 'None',
    hoaFees: '',
    propertyTaxes: '',
    availableNow: false,
    availableDate: '',
    // Agent fields will be auto-populated with current agent info
  });
  
  useEffect(() => {
    if (isEditMode && property) {
      console.log("Edit mode detected, populating form with property:", property);
      
      // Populate form fields
      populateFormData();
      
      // Set images
      if (property.mainImage) {
        console.log("Setting main image:", property.mainImage);
        setMainImage(property.mainImage);
      }
      
      if (property.images && property.images.length > 0) {
        console.log(`Setting ${property.images.length} property images`);
        setPropertyImages(property.images);
      }
    }
  }, [property, isEditMode]);
  
  // Images state
  const [mainImage, setMainImage] = useState('');
  const [propertyImages, setPropertyImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadAreaHovered, setIsUploadAreaHovered] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  
  
  
  const populateFormData = () => {
    console.log("Populating form with property data:", property);
    
    // Helper function to clean numeric strings (remove non-numeric characters)
    const cleanNumeric = (value) => {
      if (value === undefined || value === null) return '';
      return value.toString().replace(/[^0-9.]/g, '');
    };
    
    // Map property data to form fields
    setPropertyData({
      address: property.address || '',
      neighborhood: property.neighborhood?._id || property.neighborhood || '',
      price: property.price ? cleanNumeric(property.price) : '',
      bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '',
      sqft: cleanNumeric(property.sqft) || '',
      lotSize: cleanNumeric(property.lotSize) || cleanNumeric(property.lotSizeSqFt) || '',
      description: property.description || '',
      propertyType: property.propertyType || 'House',
      listingType: property.listingType || 'purchase',
      yearBuilt: property.yearBuilt?.toString() || '',
      propertyStatus: property.propertyStatus || 'For Sale',
      features: Array.isArray(property.features) ? property.features.join(', ') : property.features || '',
      // Pet policy
      allowsDogs: property.petPolicy?.allowsDogs || false,
      allowsCats: property.petPolicy?.allowsCats || false,
      petDeposit: cleanNumeric(property.petPolicy?.petDeposit) || '0',
      petRent: cleanNumeric(property.petPolicy?.petRent) || '0',
      petRestrictions: property.petPolicy?.petRestrictions || '',
      // Parking
      parkingType: property.parking?.type || 'None',
      parkingSpaces: cleanNumeric(property.parking?.spaces) || '0',
      // Laundry
      laundryType: property.laundry?.type || 'None',
      // Location
      latitude: property.location?.coordinates?.[1]?.toString() || '',
      longitude: property.location?.coordinates?.[0]?.toString() || '',
      // Additional fields that may be present
      hoaFees: cleanNumeric(property.hoaFees) || '',
      propertyTaxes: cleanNumeric(property.propertyTaxes) || '',
      availableNow: property.availableNow || false,
      availableDate: property.availableDate || '',
    });
    
    console.log("Form populated with data");
    
    // Set images
    if (property.mainImage) {
      setMainImage(property.mainImage);
    }
    
    if (property.images && property.images.length > 0) {
      setPropertyImages(property.images);
    }
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
  
    if (['price', 'hoaFees', 'propertyTaxes'].includes(name)) {
      const numeric = value.replace(/[^0-9.]/g, '');
      const withCommas = Number(numeric).toLocaleString('en-US');
  
      let finalValue = withCommas;
      if (name === 'price' && propertyData.listingType === 'rent') {
        finalValue += '/mo';
      }
  
      setPropertyData({
        ...propertyData,
        [name]: finalValue
      });
      return;
    }
  
    if (name === 'listingType') {
      const isRental = value === 'rent';
      const numeric = propertyData.price.replace(/[^0-9.]/g, '');
      const withCommas = Number(numeric).toLocaleString('en-US');
      const formattedPrice = isRental ? `${withCommas}/mo` : withCommas;
  
      setPropertyData({
        ...propertyData,
        listingType: value,
        price: formattedPrice
      });
      return;
    }
  
    // 👇 Handle everything else here (like lotSize)
    setPropertyData({
      ...propertyData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadProgress(0);
    setErrorMessage('');
    
    try {
      // Store the URLs returned from Cloudinary
      const cloudinaryUrls = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create form data for Cloudinary upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_preset', 'uploads'); 
        
        // Upload to Cloudinary
        const response = await fetch(`https://api.cloudinary.com/v1_1/dckv4itgt/image/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Cloudinary upload failed: ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Use the secure URL returned by Cloudinary
        const imageUrl = data.secure_url;
        cloudinaryUrls.push(imageUrl);
        
        setPropertyImages(prev => [...prev, imageUrl]);
        
        // Update progress
        setUploadProgress(Math.round((i + 1) / files.length * 100));
      }
      
      // Set the first uploaded image as main image if none is set
      if (!mainImage && cloudinaryUrls.length > 0) {
        setMainImage(cloudinaryUrls[0]);
      }
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setErrorMessage(`Failed to upload images: ${error.message || 'Unknown error'}`);
    }
  };

  const setAsMainImage = (imageUrl) => {
    setMainImage(imageUrl);
  };

  const removeImage = (imageUrl) => {
    setPropertyImages(propertyImages.filter(img => img !== imageUrl));
    
    // If the main image is removed, set a new main image
    if (mainImage === imageUrl) {
      setMainImage(propertyImages.length > 1 ? 
        propertyImages.find(img => img !== imageUrl) : '');
    }
  };

  const validateForm = () => {
    const requiredFields = ['address', 'price', 'bedrooms', 'bathrooms', 'sqft', 'propertyType'];
    const missingFields = requiredFields.filter(field => !propertyData[field]);
    
    if (missingFields.length > 0) {
      setErrorMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return false;
    }
    
    if (propertyImages.length < 6) {
      setErrorMessage('Please upload at least 5 property images');
      return false;
    }
    
    if (!mainImage) {
      setErrorMessage('Please select a main image');
      return false;
    }
    
    return true;
  };

  // Modify the handleSubmit function in AgentPropertyForm.js to use the new API

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log("Submitting form in " + (isEditMode ? "EDIT" : "CREATE") + " mode");
      console.log("Property ID (if editing):", property?._id);
      
      // Extract numeric price from the price string
      const extractNumericPrice = (priceString) => {
        const numericPart = priceString.replace(/[^0-9.]/g, '');
        return parseFloat(numericPart) || 0;
      };
  
      // Format the data for the API
      const formattedData = {
        address: propertyData.address.trim(),
        neighborhood: propertyData.neighborhood.trim() || '', // Make neighborhood optional
        price: propertyData.price.startsWith('$') ? 
          propertyData.price : `$${propertyData.price}`,
        numericPrice: extractNumericPrice(propertyData.price),
        bedrooms: parseInt(propertyData.bedrooms, 10),
        bathrooms: parseFloat(propertyData.bathrooms),
        sqft: parseInt(propertyData.sqft, 10),
        lotSizeSqFt: parseInt(propertyData.lotSize.replace(/[^0-9.]/g, '') || 0, 10),
        lotSizeAcres: parseFloat((propertyData.lotSize.replace(/[^0-9.]/g, '') / 43560).toFixed(4)) || 0,
        description: propertyData.description,
        propertyType: propertyData.propertyType,
        listingType: propertyData.listingType,
        availableNow: propertyData.availableNow,
        availableDate: propertyData.availableDate,
        hoaFees: parseFloat(propertyData.hoaFees || 0),
        propertyTaxes: parseFloat(propertyData.propertyTaxes || 0),
        yearBuilt: parseInt(propertyData.yearBuilt || 0, 10),
        location: {
          type: 'Point',
          coordinates: [-122.4194, 37.7749] 
        },
        features: propertyData.features ? 
          propertyData.features.split(',').map(item => item.trim()) : [],
        mainImage: mainImage,
        images: propertyImages,
        
        // Pet policy
        petPolicy: {
          allowsDogs: propertyData.allowsDogs,
          allowsCats: propertyData.allowsCats,
          petDeposit: parseInt(propertyData.petDeposit || 0, 10),
          petRent: parseInt(propertyData.petRent || 0, 10),
          petRestrictions: propertyData.petRestrictions
        },
        
        // Parking
        parking: {
          type: propertyData.parkingType,
          spaces: parseInt(propertyData.parkingSpaces || 0, 10),
        },
        
        // Laundry
        laundry: {
          type: propertyData.laundryType,
        },
        
        // Add agent information automatically from agentInfo
        listedBy: {
          agentId: agentInfo._id,
          agentName: agentInfo.name || (agentInfo.firstName + ' ' + agentInfo.lastName),
          agency: agentInfo.agency || '',
          contactPhone: agentInfo.phone || '',
          contactEmail: agentInfo.email || ''
        }
      };
      
      let response;
      
      if (isEditMode && property && property._id) {
        console.log("Updating property with ID:", property._id);
        
        // If it's a pending property that hasn't been approved yet, update it directly
        if (property.approvalStatus === 'pending') {
          console.log("Updating pending property...");
          response = await propertyService.updatePendingProperty(property._id, formattedData);
        } else {
          // Update existing property
          console.log("Updating approved property...");
          response = await propertyService.updateProperty(property._id, formattedData);
        }
        
        setSuccessMessage('Property updated successfully!');
      } else {
        // Submit new property for review instead of creating directly
        console.log("Submitting new property for review...");
        response = await propertyService.submitPropertyForReview(formattedData);
        setSuccessMessage('Property submitted for admin review! An administrator will assign a neighborhood and publish your listing soon.');
      }
      
      console.log("API response:", response);
      
      // If parent passed an onPropertyAdded callback, call it
      if (onPropertyAdded) {
        onPropertyAdded();
      }
      
      if (!isEditMode) {
        // Reset form if creating a new property
        resetForm();
      }
      
    } catch (error) {
      console.error('Error saving property:', error);
      setErrorMessage('Failed to save property: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setPropertyData({
      address: '',
      neighborhood: '',
      price: '',
      bedrooms: '',
      bathrooms: '',
      sqft: '',
      lotSize: '',
      description: '',
      propertyType: 'House',
      listingType: 'purchase',
      yearBuilt: '',
      propertyStatus: 'For Sale',
      features: '',
      allowsDogs: false,
      allowsCats: false,
      petDeposit: '0',
      petRent: '0',
      petRestrictions: '',
      parkingType: 'None',
      parkingSpaces: '0',
      laundryType: 'None',
    });
    
    setMainImage('');
    setPropertyImages([]);
    setUploadProgress(0);
  };

  return (
    <div style={{ color: '#1a1a1a' }}>
      <style>{`
        /* Property Form — modern light refresh matching admin portal */
        .content-container {
          background-color: transparent !important;
          max-width: 960px;
        }
        .section-title {
          color: #111 !important;
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 20px;
          text-align: left;
          border: none !important;
          border-bottom: none !important;
          padding-bottom: 0 !important;
          letter-spacing: -0.02em;
        }
        .section-title::before,
        .section-title::after {
          display: none !important;
          content: none !important;
        }
        .alert {
          padding: 11px 14px;
          border-radius: 9px;
          font-size: 0.875rem;
          margin-bottom: 16px;
          font-weight: 500;
        }
        .alert-success {
          background-color: #ecfdf5 !important;
          color: #047857 !important;
          border: 1px solid #a7f3d0 !important;
        }
        .alert-danger {
          background-color: #fef2f2 !important;
          color: #b91c1c !important;
          border: 1px solid #fecaca !important;
        }
        .form-section {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 14px;
          padding: 22px 24px;
          margin-bottom: 16px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        }
        .form-section-header {
          margin: 0 0 18px 0;
          padding: 0 0 12px 0;
          border-bottom: 1px solid #f0f0f0;
          background: transparent !important;
        }
        .form-section-title {
          color: #111 !important;
          font-size: 0.95rem;
          font-weight: 600;
          margin: 0;
          letter-spacing: -0.01em;
          background: transparent !important;
          text-align: left;
        }
        .form-section-body {
          padding-top: 2px;
          background-color: transparent !important;
        }
        .form-label {
          color: #6b7280 !important;
          font-weight: 600;
          font-size: 0.68rem;
          margin-bottom: 6px;
          display: block;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .form-label-required::after {
          content: " *";
          color: #dc2626;
        }
        .form-control,
        .form-control input,
        .form-control textarea,
        .form-control select {
          background-color: #ffffff !important;
          border: 1px solid #e5e7eb !important;
          color: #111 !important;
          padding: 9px 11px;
          border-radius: 9px;
          transition: border-color 0.12s, box-shadow 0.12s;
          width: 100%;
          font-size: 0.9rem;
          font-family: inherit;
        }
        .form-control:focus,
        .form-control input:focus,
        .form-control textarea:focus,
        .form-control select:focus {
          border-color: #111 !important;
          background-color: #ffffff !important;
          outline: none;
          box-shadow: 0 0 0 3px rgba(17, 17, 17, 0.06);
        }
        .form-control option {
          background-color: #ffffff;
          color: #111;
        }
        .form-control::placeholder {
          color: #9ca3af;
        }
        textarea.form-control {
          min-height: 88px;
          resize: vertical;
        }
        .checkbox-group {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #374151 !important;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.88rem;
        }
        .checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #111;
        }
        .form-row {
          display: grid;
          gap: 14px;
          margin-bottom: 0;
        }
        .form-row-2 {
          grid-template-columns: repeat(2, 1fr);
        }
        .form-row-3 {
          grid-template-columns: repeat(3, 1fr);
        }
        .form-row-4 {
          grid-template-columns: repeat(4, 1fr);
        }
        .form-group {
          margin-bottom: 16px;
        }

        /* Image upload area */
        .image-upload-area,
        .image-upload-section {
          background-color: #fafafa !important;
          border: 1.5px dashed #d1d5db !important;
          border-radius: 12px;
          padding: 32px 20px;
          text-align: center;
          transition: all 0.15s;
        }
        .image-upload-area:hover,
        .image-upload-section:hover {
          border-color: #9ca3af !important;
          background-color: #f5f5f5 !important;
        }
        .image-upload-section * {
          background-color: transparent !important;
        }
        .upload-icon {
          color: #9ca3af !important;
          margin-bottom: 10px !important;
          display: flex !important;
          justify-content: center !important;
        }
        .upload-icon svg {
          color: #9ca3af !important;
          stroke: #9ca3af !important;
          width: 36px !important;
          height: 36px !important;
        }
        .upload-text {
          color: #374151 !important;
          font-size: 0.88rem !important;
          margin-bottom: 12px !important;
          font-weight: 500 !important;
        }
        .upload-hint {
          color: #9ca3af !important;
          font-size: 0.75rem !important;
          margin-top: 10px !important;
        }
        .upload-button,
        .upload-btn {
          display: inline-block !important;
          padding: 7px 16px !important;
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 8px !important;
          cursor: pointer !important;
          transition: all 0.12s !important;
          font-size: 0.82rem !important;
          font-weight: 600 !important;
        }
        .upload-button:hover,
        .upload-btn:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }

        .image-preview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
          margin-top: 16px;
        }
        .image-preview-item {
          position: relative;
          border-radius: 10px;
          overflow: hidden;
          aspect-ratio: 1;
          background-color: #f4f4f5;
          border: 1px solid #e5e7eb;
        }
        .image-preview-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .remove-image-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          background-color: rgba(0, 0, 0, 0.7);
          color: #ffffff;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.12s;
          font-size: 0.9rem;
          line-height: 1;
        }
        .remove-image-btn:hover {
          background-color: #000000;
          transform: scale(1.08);
        }
        .image-actions {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.75), transparent);
          padding: 8px;
          display: flex;
          gap: 6px;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.15s;
        }
        .image-preview-item:hover .image-actions {
          opacity: 1;
        }
        .image-action-button {
          background: #ffffff;
          color: #111;
          border: none;
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.12s;
        }
        .image-action-button:hover {
          background: #f9fafb;
        }
        .main-image-badge {
          position: absolute;
          top: 6px;
          left: 6px;
          background: #111;
          color: #ffffff;
          padding: 3px 9px;
          border-radius: 6px;
          font-size: 0.62rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          z-index: 2;
        }
        .progress-container {
          height: 4px;
          background: #f0f0f0;
          border-radius: 999px;
          overflow: hidden;
          margin-top: 12px;
        }
        .progress-bar {
          height: 100%;
          background: #111;
          border-radius: 999px;
          transition: width 0.2s ease;
        }
        .progress-text {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 6px;
        }

        /* Buttons */
        .btn-primary {
          background-color: #111 !important;
          color: #ffffff !important;
          border: 1px solid #111 !important;
          padding: 10px 22px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.12s;
          font-size: 0.88rem;
          letter-spacing: -0.005em;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .btn-primary:hover:not(:disabled) {
          background-color: #000 !important;
          border-color: #000 !important;
          transform: translateY(-1px);
        }
        .btn-primary:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .btn-secondary {
          background-color: #ffffff !important;
          color: #111 !important;
          border: 1px solid #e5e7eb !important;
          padding: 10px 22px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.12s;
          font-size: 0.88rem;
        }
        .btn-secondary:hover {
          background-color: #f9fafb !important;
          border-color: #d1d5db !important;
        }
        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
          padding-top: 18px;
          border-top: 1px solid #f0f0f0;
        }

        /* Spinner */
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .loading-spinner {
          border: 2.5px solid rgba(255, 255, 255, 0.3) !important;
          border-top-color: #ffffff !important;
          border-radius: 50%;
          width: 14px;
          height: 14px;
          animation: spin 0.7s linear infinite;
          display: inline-block;
          vertical-align: middle;
        }

        /* Page header — defensive resets */
        .page-header-form {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: none !important;
          border-bottom: none !important;
        }
        .page-header-form::before,
        .page-header-form::after,
        .page-header-form *::before,
        .page-header-form *::after {
          display: none !important;
          content: none !important;
        }
        .page-header-form *,
        .page-header-form h2 {
          border: none !important;
          border-bottom: none !important;
        }
        .invite-buyers-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 9px 18px;
          background-color: #ffffff;
          color: #111;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.12s;
        }
        .invite-buyers-btn:hover {
          background-color: #f9fafb;
          border-color: #d1d5db;
        }
        .image-upload-input {
          display: none !important;
        }

        @media (max-width: 768px) {
          .form-row-2,
          .form-row-3,
          .form-row-4 {
            grid-template-columns: 1fr;
          }
          .page-header-form {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
        }
      `}</style>
      <div className="content-container">
      
      
      {successMessage && (
        <div className="alert alert-success mb-4">
          {successMessage}
        </div>
      )}
      
      {errorMessage && (
        <div className="alert alert-danger mb-4">
          {errorMessage}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Basic Property Information Section */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Basic Information</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row form-row-4">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Address
                </label>
                <input 
                  type="text" 
                  name="address"
                  value={propertyData.address}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="123 Main St, City, State ZIP"
                  required
                />
              </div>
              
          
  
              <div className="form-group">
  <label className="form-label form-label-required">
    Price ($)
  </label>
  <input 
    type="text" 
    name="price"
    value={propertyData.price}
    onChange={handleInputChange}
    className="form-control"
    placeholder="725,000 or 1,500/mo"
    required
  />
</div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Listing Type
                </label>
                <select 
                  name="listingType"
                  value={propertyData.listingType}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="purchase">Purchase</option>
                  <option value="rent">Rent</option>
                </select>
              </div>
            </div>
            
            <div className="form-row form-row-4">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Property Type
                </label>
                <select 
                  name="propertyType"
                  value={propertyData.propertyType}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="House">House</option>
                  <option value="Condo">Condo</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Townhouse">Townhouse</option>
                  <option value="Multifamily">Multi-Family</option>
                  <option value="ManufacturedMobile">Manufactured/Mobile</option>
                </select>
              </div>
                
              <div className="form-group">
                <label className="form-label form-label-required">
                  Bedrooms
                </label>
                <input 
                  type="number" 
                  name="bedrooms"
                  value={propertyData.bedrooms}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="3"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Bathrooms
                </label>
                <input 
                  type="number" 
                  step="0.5"
                  name="bathrooms"
                  value={propertyData.bathrooms}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="2.5"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label form-label-required">
                  Square Feet
                </label>
                <input 
                  type="number" 
                  name="sqft"
                  value={propertyData.sqft}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="2,000"
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label form-label-required">
                  Description
                </label>
                <textarea 
  rows={4}
  name="description"
  value={propertyData.description}
  onChange={handleInputChange}
  className="form-control description-textarea"
  placeholder="Describe the property features, location, and unique selling points..."
  required
></textarea>
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Details Section */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Additional Details</h3>
          </div>
          <div className="form-section-body">
          {propertyData.listingType === 'purchase' && (
  <div className="form-row form-row-3">
    <div className="form-group">
      <label className="form-label form-label-required">Year Built</label>
      <input 
        type="number" 
        name="yearBuilt"
        value={propertyData.yearBuilt}
        onChange={handleInputChange}
        className="form-control"
        placeholder="2000"
        required
      />
    </div>

    <div className="form-group">
  <label className="form-label form-label-required">Lot Size (sq ft)</label>
  <input 
    type="number" 
    name="lotSize"
    value={propertyData.lotSize}
    onChange={handleInputChange}
    className="form-control"
    placeholder="5000"
    required
  />

  {/* Acres readout */}
  {propertyData.lotSize && !isNaN(propertyData.lotSize) && (
    <p className="text-xs text-gray-500 mt-1">
      ~{(parseFloat(propertyData.lotSize.replace(/[^0-9.]/g, '')) / 43560).toFixed(2)} acres
    </p>
  )}
</div>


    <div className="form-group">
      <label className="form-label form-label-required">HOA Fees ($/mo)</label>
      <input 
        type="text" 
        name="hoaFees"
        value={propertyData.hoaFees || ''}
        onChange={handleInputChange}
        className="form-control"
        placeholder="150"
        required
      />
    </div>
  </div>
)}

{propertyData.listingType === 'purchase' && (
  <div className="form-row form-row-3">
    <div className="form-group">
      <label className="form-label form-label-required">Property Taxes ($/year)</label>
      <input 
        type="text" 
        name="propertyTaxes"
        value={propertyData.propertyTaxes || ''}
        onChange={handleInputChange}
        className="form-control"
        placeholder="3200"
        required
      />
    </div>
  </div>
)}
            
            {/* Pet Policy (for rentals) */}
            {propertyData.listingType === 'rent' && (
              <div className="form-row form-row-3">
                <div className="form-group">
                  <label className="form-label">Dogs Allowed</label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="allowsDogs"
                      name="allowsDogs"
                      checked={propertyData.allowsDogs}
                      onChange={handleInputChange}
                      className="form-checkbox"
                    />
                    <label htmlFor="allowsDogs" className="ml-2">
                      Property allows dogs
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Cats Allowed</label>
                  <div className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id="allowsCats"
                      name="allowsCats"
                      checked={propertyData.allowsCats}
                      onChange={handleInputChange}
                      className="form-checkbox"
                    />
                    <label htmlFor="allowsCats" className="ml-2">
                      Property allows cats
                    </label>
                  </div>
                </div>
                
                
              </div>
            )}

{propertyData.listingType === 'rent' && (
  <div className="form-row form-row-3">
    {/* Available Now Checkbox */}
    <div className="form-group">
      <label className="form-label">Available Now</label>
      <div className="flex items-center mt-2">
        <input
          type="checkbox"
          id="availableNow"
          name="availableNow"
          checked={propertyData.availableNow}
          onChange={(e) => {
            const checked = e.target.checked;
            setPropertyData((prev) => ({
              ...prev,
              availableNow: checked,
              availableDate: checked ? new Date().toISOString().split('T')[0] : prev.availableDate,
            }));
          }}
          className="form-checkbox"
        />
        <label htmlFor="availableNow" className="ml-2">
          Property is available immediately
        </label>
      </div>
    </div>

    {/* Available Date Input */}
    <div className="form-group">
      <label className="form-label">Available Date</label>
      <input
        type="date"
        name="availableDate"
        value={propertyData.availableDate}
        onChange={handleInputChange}
        className="form-control"
        disabled={propertyData.availableNow}
      />
    </div>
  </div>
)}

            
            {/* Parking and Laundry */}
            <div className="form-row form-row-3">
              <div className="form-group">
                <label className="form-label form-label-required">Parking Type</label>
                <select
                  name="parkingType"
                  value={propertyData.parkingType}
                  onChange={handleInputChange}
                  className="form-control"
                  required
                >
                  <option value="N/A">None</option>
                  <option value="Attached">Attached Garage</option>
                  <option value="Detached">Detached Garage</option>
                  <option value="Assigned">Assigned</option>
                  <option value="Carport">Carport</option>
                  <option value="Covered">Covered</option>
                  <option value="Parking Garage">Parking Garage</option>

                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Number of Parking Spaces</label>
                <input
                  type="number"
                  name="parkingSpaces"
                  value={propertyData.parkingSpaces}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="0"
                  min="0"
                />
              </div>
              
              {propertyData.listingType === 'rent' && (
  <div className="form-group">
    <label className="form-label form-label-required">Laundry Type</label>
    <select
      name="laundryType"
      value={propertyData.laundryType}
      onChange={handleInputChange}
      className="form-control"
      required
    >
      <option value="None">None</option>
      <option value="In Unit">In Unit</option>
      <option value="In Building">In Building</option>
      <option value="Hookups">Hookups Only</option>
      <option value="Shared">Shared</option>

    </select>
  </div>
)}
            </div>
          </div>
        </div>
        
        
        {/* Property Features */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Property Features</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Amenities and Features
                </label>
                <input 
                  type="text" 
                  name="features"
                  value={propertyData.features}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Fireplace, Hardwood Floors, Granite Countertops"
                />
                <p className="mt-1 text-text-light text-xs">
                  Comma-separated list of features
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Property Images - Always shown */}
        <div className="form-section">
          <div className="form-section-header">
            <h3 className="form-section-title">Property Images *</h3>
          </div>
          <div className="form-section-body">
            <label className="form-label form-label-required mb-2">
              Upload Images (minimum 6 required)
            </label>
            
            <div 
              className={`image-upload-area ${isUploadAreaHovered ? 'hovered' : ''}`}
              onMouseEnter={() => setIsUploadAreaHovered(true)}
              onMouseLeave={() => setIsUploadAreaHovered(false)}
            >
              <div className="upload-icon">
                <svg
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                  width="36"
                  height="36"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4h-12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              
              <p className="upload-text">Drag and drop your images here, or</p>
              
              <label 
                htmlFor="image-upload" 
                className="upload-button"
              >
                Browse Files
                <input 
                  id="image-upload" 
                  name="image-upload" 
                  type="file" 
                  className="image-upload-input" 
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </label>
              
              <p className="upload-hint">
                PNG, JPG, GIF up to 10MB each
              </p>
            </div>
            
            {uploadProgress > 0 && (
              <div className="mt-4">
                <div className="progress-container">
                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <div className="progress-text">
                  Uploading: {uploadProgress}%
                </div>
              </div>
            )}
            
            {/* Uploaded Images Preview */}
            {propertyImages.length > 0 && (
              <div className="image-preview-container">
                <h4 className="text-md font-medium mb-2">Uploaded Images</h4>
                <p className="text-text-light text-xs mb-2">
                  Select one image as the main image by clicking "Set as Main"
                </p>
                <div className="image-preview-grid">
                  {propertyImages.map((imageUrl, index) => (
                    <div key={index} className="image-preview-item">
                      {imageUrl === mainImage && (
                        <div className="main-image-badge">Main</div>
                      )}
                      <img
                        src={imageUrl}
                        alt={`Property ${index + 1}`}
                        className="image-preview"
                      />
                      <div className="image-actions">
                        {imageUrl !== mainImage ? (
                          <button
                            type="button"
                            onClick={() => setAsMainImage(imageUrl)}
                            className="image-action-button"
                          >
                            Set as Main
                          </button>
                        ) : (
                          <span className="image-action-button">
                            Main Image
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(imageUrl)}
                          className="image-action-button"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            onClick={resetForm}
            className="btn btn-secondary mr-2"
          >
            Clear Form
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner mr-2" style={{ width: '18px', height: '18px' }}></div>
                {isEditMode ? 'Updating...' : 'Saving...'}
              </>
            ) : (isEditMode ? 'Update Property' : 'Save Property')}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
};

export default AgentPropertyForm;